import { CloudUpload, FileText } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useDocumentKinds, useUploadPersonnelDocument } from '../../api'
import { fileSize, todayIso } from '../../lib/fileExport'
import { Field, ModalFooter } from './Field'
import { apiErrorMessage } from '@/i18n'

export function BelgeYukleModal({ personelId, onClose }: { personelId: string; onClose: () => void }) {
  const { t } = useTranslation()
  const kinds = useDocumentKinds()
  const [kategori, setKategori] = useState('')
  const kind = kategori || kinds.data?.[0]?.key || ''
  const [file, setFile] = useState<File | null>(null)
  const [ad, setAd] = useState('')
  const [gecerlilik, setGecerlilik] = useState('')
  // Geçmiş tarihli belge (eski sözleşme, bordro vb.) için düzenlenme tarihi; boşsa yükleme tarihi esas alınır.
  const [belgeTarihi, setBelgeTarihi] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const upload = useUploadPersonnelDocument()

  const errFile = submitted && !file ? t('hr.personel.actions.required') : undefined
  // Başlık opsiyonel: boşsa belge türünün adı kullanılır.
  const name = ad.trim() || kinds.data?.find((k) => k.key === kind)?.title || ''

  const submit = () => {
    setSubmitted(true)
    if (!file || !kind) return
    upload.mutate(
      { employeeId: personelId, kind, file, title: ad.trim() || undefined, validUntil: gecerlilik || undefined, documentDate: belgeTarihi || undefined },
      {
        onSuccess: () => {
          toast.success(t('hr.personel.actions.belge.uploaded', { name }))
          onClose()
        },
        onError: (error) => toast.error(apiErrorMessage(error)),
      },
    )
  }

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      icon={<CloudUpload />}
      title={t('hr.personel.actions.belge.title')}
      description={t('hr.personel.actions.belge.hint')}
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
      footer={<ModalFooter onCancel={onClose} submitLabel={t('hr.personel.actions.belge.upload')} />}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="belge-file" label={t('hr.personel.actions.belge.dosya')} error={errFile} className="sm:col-span-2">
          <label
            htmlFor="belge-file"
            className={cn(
              'flex cursor-pointer items-center gap-3 rounded-xl border border-dashed bg-muted/30 p-4 text-sm transition-colors hover:bg-muted/60',
              errFile ? 'border-destructive/60' : 'border-border',
              file && 'border-success/40 bg-success/5',
            )}
          >
            <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-card text-muted-foreground shadow-xs">
              {file ? <FileText className="size-4 text-success" /> : <CloudUpload className="size-4" />}
            </span>
            <span className="min-w-0">
              <span className="block truncate font-medium">{file ? file.name : t('hr.personel.actions.belge.dosyaSec')}</span>
              <span className="block text-xs text-muted-foreground">{file ? fileSize(file.size) : 'PDF, JPG, PNG, WEBP · 15 MB'}</span>
            </span>
          </label>
          <input
            id="belge-file"
            type="file"
            className="sr-only"
            accept="application/pdf,image/jpeg,image/png,image/webp"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null
              setFile(f)
              if (f && !ad.trim()) setAd(f.name.replace(/\.[^.]+$/, ''))
            }}
          />
        </Field>
        <Field id="belge-ad" label={t('hr.personel.actions.belge.ad')}>
          <Input id="belge-ad" className="h-10 rounded-xl" value={ad} placeholder={name} maxLength={150} onChange={(e) => setAd(e.target.value)} />
        </Field>
        <Field id="belge-kat" label={t('hr.personel.actions.belge.kategori')}>
          <Select
            id="belge-kat"
            size="lg"
            className="w-full rounded-xl"
            value={kind}
            onChange={setKategori}
            loading={kinds.isLoading}
            options={(kinds.data ?? []).map((k) => ({ value: k.key, label: k.title }))}
          />
        </Field>
        <Field id="belge-tarih" label={t('hr.personel.actions.belge.belgeTarihi')}>
          <DateField id="belge-tarih" className="h-10 rounded-xl" value={belgeTarihi} max={todayIso()} onChange={setBelgeTarihi} clearable />
        </Field>
        <Field id="belge-gec" label={t('hr.personel.actions.belge.gecerlilik')}>
          <DateField id="belge-gec" className="h-10 rounded-xl" value={gecerlilik} min={belgeTarihi || undefined} onChange={setGecerlilik} clearable />
        </Field>
      </div>
    </Modal>
  )
}
